import { useAuth } from "react-oidc-context";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { Layout } from "./components/Layout";
import { REDIRECTS } from "./nav/routes";
import type { AppConfig } from "./config";
import {
  NotProvisioned,
  SignIn,
  isNotProvisioned,
  shouldRetryRelink,
  startSignIn,
} from "./pages/SignIn";
import { Audit } from "./pages/governance/Audit";
import { Permissions } from "./pages/governance/Permissions";
import { FleetPosture } from "./pages/overview/FleetPosture";
import { CapsTier } from "./pages/policy/CapsTier";
import { LabeebTier } from "./pages/policy/LabeebTier";
import { Flags } from "./pages/policy/Flags";
import { Tiers } from "./pages/policy/Tiers";
import { Access } from "./pages/policy/Access";
import { Tenant } from "./pages/tenants/Tenant";
import { TenantHistory } from "./pages/tenants/TenantHistory";
import { TenantUsage } from "./pages/tenants/TenantUsage";
import { TenantUser } from "./pages/tenants/TenantUser";
import { TenantUsers } from "./pages/tenants/TenantUsers";
import { Tenants } from "./pages/tenants/Tenants";
import { Button } from "./f7";
import { useActivityTracking } from "./hooks/useActivityTracking";

export function App({ config }: { config: AppConfig }) {
  const auth = useAuth();
  // Unconditionally, before any early return — a hook called inside the
  // authenticated branch would change the hook order between renders. It is
  // harmless while signed out: nothing reads the stamp until a write.
  useActivityTracking();

  // NO signinSilent() here, and that is deliberate.
  //
  // Silent renew loads the authorize endpoint in a hidden iframe and depends on
  // Cognito's session cookie, which is THIRD-PARTY relative to this origin.
  // Browsers block it, so the frame hangs until oidc-client-ts times it out —
  // on the path EVERY signed-out visitor takes. Going straight to the sign-in
  // screen renders instantly and costs one click.

  if (auth.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">Signing in…</p>
      </div>
    );
  }

  if (auth.error) {
    // The roster gate refusing someone is not a fault — it is an answer, and
    // it deserves a screen that says what to do rather than a red stack of
    // Cognito's wording.
    if (isNotProvisioned(auth.error)) return <NotProvisioned />;

    // Cognito fails the very sign-in that links a Google identity to an
    // existing user, and succeeds immediately after. Retrying once, guarded,
    // is the difference between "it worked" and "it was broken the first
    // time" for every new joiner. See SignIn.tsx.
    if (shouldRetryRelink(auth.error)) {
      startSignIn(auth, config.googleEnabled);
      return (
        <div className="flex min-h-full items-center justify-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">Signing in…</p>
        </div>
      );
    }

    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-md border border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 p-6 text-center shadow-lg">
          <h1 className="text-lg font-semibold">Sign-in failed</h1>
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{auth.error.message}</p>
          <Button onClick={() => startSignIn(auth, config.googleEnabled)} className="mt-4">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) return <SignIn config={config} />;

  return (
    <BrowserRouter>
      <Layout config={config}>
        <Routes>
          {/* Overview */}
          <Route path="/" element={<FleetPosture config={config} />} />

          {/* Tenants. Static paths BEFORE :ref — otherwise "access" can match
              as a business reference and render a merchant page for a merchant
              that does not exist, which surfaces as an API 404 and reads like a
              backend fault. */}
          <Route path="/tenants" element={<Tenants config={config} />} />
          <Route path="/policy/access" element={<Access config={config} />} />
          <Route path="/tenants/:ref" element={<Tenant config={config} />} />
          <Route path="/tenants/:ref/usage" element={<TenantUsage config={config} />} />
          <Route path="/tenants/:ref/history" element={<TenantHistory config={config} />} />
          <Route path="/tenants/:ref/users" element={<TenantUsers config={config} />} />
          <Route path="/tenants/:ref/users/:email" element={<TenantUser config={config} />} />

          {/* Policy */}
          {/* Tiers & limits lists both products' tiers. Each tier opens its
              own page: MCP's at /policy/caps/{tier}, Labeeb's at
              /policy/tiers/{tier}. */}
          <Route path="/policy/tiers" element={<Tiers config={config} />} />
          <Route path="/policy/caps/:tier" element={<CapsTier config={config} />} />
          <Route path="/policy/tiers/:tier" element={<LabeebTier config={config} />} />
          <Route path="/policy/settings" element={<Flags config={config} />} />

          {/* Governance */}
          <Route path="/governance/permissions" element={<Permissions config={config} />} />
          <Route path="/governance/audit" element={<Audit config={config} />} />

          {/* Every URL this console has ever served still resolves. */}
          {REDIRECTS.map((r) => (
            <Route key={r.from} path={r.from} element={<Redirect to={r.to} />} />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}


/**
 * A redirect that carries its params across.
 *
 * The previous version read `window.location.pathname` and split on "/", which
 * mangled any reference needing URL-encoding and bypassed the router entirely.
 * Reading `useParams` and re-encoding means the old URL and the new one agree
 * about what the reference actually is.
 */
function Redirect({ to }: { to: string }) {
  const params = useParams();
  const path = to.replace(/:(\w+)/g, (_, key: string) =>
    encodeURIComponent(params[key] ?? ""),
  );
  return <Navigate to={path} replace />;
}
